import React, {
  useMemo,
  useState,
  useEffect,
  ComponentType,
  PropsWithChildren,
} from 'react';

import PropTypes from 'prop-types';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import useClientSideDataModification from './hooks/useClientSideDataModification';
import { Pagination, Role } from './types';

type RolesResponse = {
  pagination: Pagination;
  results: Role[];
};

type RolesContextValue = {
  data: Role[];
  loading: boolean;
  error?: null | Error;
  retry(): void;
  addRoles(roles: Role[]): void;
  updateRole(role: Role): void;
  removeRoleById(id: Role['id']): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for roles list.
 */
const RolesContext = getOrCreateGlobalContext<RolesContextValue>(
  CONTEXT_NAMES.RolesContext,
);

const useRolesContext = () =>
  useContextWithValidation(RolesContext, {
    hook: 'useRoles',
    provider: 'RolesProvider',
  });

export const useRoles = () => {
  const { setEnabled, ...rest } = useRolesContext();
  useEffect(() => {
    if (setEnabled) {
      setEnabled(true);
    }
  }, [setEnabled]);
  return rest;
};

const PAGE_LIMIT = 200;

export const RolesProvider: ComponentType<PropsWithChildren> = ({
  children,
}) => {
  const [enabled, setEnabled] = useState(false);
  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminApiUrl();
  const url =
    enabled && projectId
      ? `${adminApiUrl}/v1/projects/${projectId}/roles?limit=${PAGE_LIMIT}`
      : '';

  const {
    loading: loadingData,
    error,
    data: dataFromBE,
    retry,
    clearSessionStorage,
  } = useGetAllPaginatedData<RolesResponse>({
    url,
  });

  const rolesFromBE = useMemo(() => {
    return (
      dataFromBE &&
      removeOldDuplicatesBy(
        dataFromBE.flatMap((page) => page.results),
        (item) => item.id,
      )
    );
  }, [dataFromBE]);

  const {
    modifyData,
    addData: addRoles,
    updateEntity: updateRole,
    removeEntityById: removeRoleById,
  } = useClientSideDataModification<Role>(clearSessionStorage);

  const loading = loadingData || !enabled;
  const data = useMemo(
    () => modifyData(rolesFromBE),
    [modifyData, rolesFromBE],
  );

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      addRoles,
      updateRole,
      removeRoleById,
      setEnabled: enabled ? undefined : setEnabled,
    }),
    [
      addRoles,
      updateRole,
      data,
      error,
      loading,
      removeRoleById,
      retry,
      enabled,
    ],
  );

  return (
    <RolesContext.Provider value={value}>{children}</RolesContext.Provider>
  );
};

RolesProvider.displayName = 'RolesProvider';

RolesProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
};
